export const services = {
  web: {
    title: "Web & Digital",
    items: [
      "Business Websites & Web Apps",
      "E-Commerce (Shopify, WooCommerce, Custom)",
      "SEO & Organic Growth",
      "Google & Meta Ads Management",
      "Social Media Management",
      "Branding & Visual Identity",
      "UI/UX Design"
    ],
    slug: "/services/web-development"
  },
  software: {
    title: "Software & Enterprise",
    items: [
      "Custom CRM Development",
      "Custom ERP Development",
      "Web & Mobile App Development",
      "Backend & API Development",
      "Cloud & DevOps (AWS, GCP)",
      "Database Architecture",
      "Third-Party Integrations"
    ],
    slug: "/services/crm-development"
  },
  ai: {
    title: "AI & Automation",
    items: [
      "AI Agents & Chatbots",
      "AI Call Centre (Inbound + Outbound)",
      "LLM Integration (GPT-4o, Claude, Gemini)",
      "RAG Pipelines & Vector Databases",
      "RPA & Workflow Automation",
      "AI Strategy Consulting",
      "Custom AI Model Development"
    ],
    slug: "/services/ai-agents"
  }
};

export type ServicePillar =
  | "web-digital"
  | "enterprise-systems"
  | "ai-solutions"
  | "custom-software"
  | "industry-solutions"
  | "strategic-services";

export interface Service {
  slug: string;
  name: string;
  shortDescription: string;
  longDescription: string;
  icon: string;
  pillar: ServicePillar;
  features: string[];
  technologies: string[];
  deliverables: string[];
  timelineWeeks: { min: number; max: number };
  priceRange: {
    INR: { min: number; max: number };
    USD: { min: number; max: number };
    GBP: { min: number; max: number };
    AED: { min: number; max: number };
    EUR: { min: number; max: number };
  };
  relatedServices: string[];
}

const basePrice = {
  INR: { min: 120000, max: 1500000 },
  USD: { min: 2500, max: 20000 },
  GBP: { min: 1900, max: 16000 },
  AED: { min: 9000, max: 73000 },
  EUR: { min: 2200, max: 18500 },
};

function createService(
  slug: string,
  name: string,
  pillar: ServicePillar,
  icon: string,
  relatedServices: string[]
): Service {
  return {
    slug,
    name,
    shortDescription: `${name} for growth-stage and enterprise businesses.`,
    longDescription: `OWL delivers ${name.toLowerCase()} with measurable outcomes, strong engineering standards, and transparent execution suitable for international markets.`,
    icon,
    pillar,
    features: [
      "Discovery and planning",
      "Execution with milestone tracking",
      "QA and performance validation",
      "Launch support and optimization",
    ],
    technologies: ["React", "TypeScript", "Node.js", "Cloud"],
    deliverables: ["Strategy document", "Working implementation", "Documentation", "Handover"],
    timelineWeeks: { min: 2, max: 16 },
    priceRange: basePrice,
    relatedServices,
  };
}

export const serviceCatalog: Service[] = [
  createService("custom-website-development", "Custom Website Development", "web-digital", "Globe", ["ui-ux-design", "website-redesign"]),
  createService("wordpress-development", "WordPress Development", "web-digital", "Layout", ["custom-website-development", "seo-services"]),
  createService("shopify-ecommerce", "Shopify/E-commerce Development", "web-digital", "ShoppingCart", ["landing-page-design", "digital-marketing-seo"]),
  createService("landing-page-design", "Landing Page Design", "web-digital", "PanelTop", ["ui-ux-design", "digital-marketing-seo"]),
  createService("ui-ux-design", "UI/UX Design", "web-digital", "PenTool", ["website-redesign", "progressive-web-apps"]),
  createService("website-redesign", "Website Redesign", "web-digital", "RefreshCw", ["custom-website-development", "seo-services"]),
  createService("progressive-web-apps", "Progressive Web Apps", "web-digital", "Smartphone", ["mobile-app-development", "api-development-integration"]),

  createService("erp-development", "ERP Development", "enterprise-systems", "Building2", ["crm-solutions", "database-architecture"]),
  createService("crm-solutions", "CRM Solutions", "enterprise-systems", "Users", ["erp-development", "business-process-automation"]),
  createService("business-process-automation", "Business Process Automation", "enterprise-systems", "Workflow", ["enterprise-portals", "database-architecture"]),
  createService("enterprise-portals", "Enterprise Portals", "enterprise-systems", "Briefcase", ["crm-solutions", "api-development-integration"]),
  createService("database-architecture", "Database Architecture", "enterprise-systems", "Database", ["erp-development", "cloud-infrastructure"]),

  createService("ai-chatbots", "AI Chatbots", "ai-solutions", "Bot", ["natural-language-processing", "ai-powered-analytics"]),
  createService("machine-learning-integration", "Machine Learning Integration", "ai-solutions", "Brain", ["predictive-modeling", "ai-powered-analytics"]),
  createService("natural-language-processing", "Natural Language Processing", "ai-solutions", "MessageCircle", ["ai-chatbots", "predictive-modeling"]),
  createService("computer-vision", "Computer Vision", "ai-solutions", "ScanEye", ["machine-learning-integration", "ai-powered-analytics"]),
  createService("ai-powered-analytics", "AI-Powered Analytics", "ai-solutions", "LineChart", ["predictive-modeling", "data-analytics-bi"]),
  createService("predictive-modeling", "Predictive Modeling", "ai-solutions", "Gauge", ["machine-learning-integration", "ai-powered-analytics"]),

  createService("mobile-app-development", "Mobile App Development (iOS/Android)", "custom-software", "Smartphone", ["saas-product-development", "api-development-integration"]),
  createService("saas-product-development", "SaaS Product Development", "custom-software", "Rocket", ["cloud-infrastructure", "devops-ci-cd"]),
  createService("api-development-integration", "API Development & Integration", "custom-software", "Network", ["cloud-infrastructure", "database-architecture"]),
  createService("cloud-infrastructure", "Cloud Infrastructure", "custom-software", "Cloud", ["devops-ci-cd", "api-development-integration"]),
  createService("devops-ci-cd", "DevOps & CI/CD", "custom-software", "GitBranch", ["cloud-infrastructure", "saas-product-development"]),

  createService("healthcare-tech", "Healthcare Tech", "industry-solutions", "HeartPulse", ["erp-development", "ai-powered-analytics"]),
  createService("fintech-solutions", "FinTech Solutions", "industry-solutions", "Landmark", ["crm-solutions", "predictive-modeling"]),
  createService("edtech-platforms", "EdTech Platforms", "industry-solutions", "GraduationCap", ["custom-website-development", "mobile-app-development"]),
  createService("real-estate-tech", "Real Estate Tech", "industry-solutions", "Building", ["crm-solutions", "ai-chatbots"]),
  createService("logistics-supply-chain", "Logistics & Supply Chain Tech", "industry-solutions", "Truck", ["erp-development", "business-process-automation"]),

  createService("digital-marketing-seo", "Digital Marketing & SEO", "strategic-services", "Search", ["brand-strategy", "growth-hacking"]),
  createService("brand-strategy", "Brand Strategy", "strategic-services", "BadgeCheck", ["digital-marketing-seo", "product-strategy"]),
  createService("technical-consulting", "Technical Consulting", "strategic-services", "Lightbulb", ["product-strategy", "cloud-infrastructure"]),
  createService("product-strategy", "Product Strategy", "strategic-services", "Map", ["technical-consulting", "saas-product-development"]),
  createService("growth-hacking", "Growth Hacking", "strategic-services", "TrendingUp", ["digital-marketing-seo", "ai-powered-analytics"]),
];

export const serviceCatalogBySlug = Object.fromEntries(
  serviceCatalog.map((service) => [service.slug, service])
) as Record<string, Service>;

export const getServiceBySlug = (slug: string) => serviceCatalogBySlug[slug];

export const getServicesByPillar = (pillar: ServicePillar) =>
  serviceCatalog.filter((service) => service.pillar === pillar);
